import { Sparkles, Palette } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

export type CreativeMode = "auto" | "manual";

interface ProductCreativeStylePickerProps {
  mode: CreativeMode | null;
  customDirection: string;
  onModeChange: (mode: CreativeMode) => void;
  onCustomDirectionChange: (value: string) => void;
}

export function ProductCreativeStylePicker({
  mode,
  customDirection,
  onModeChange,
  onCustomDirectionChange,
}: ProductCreativeStylePickerProps) {
  const { t } = useLanguage();

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.productCreative.stylePickerTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.productCreative.stylePickerHint}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <ModeCard
          icon={Sparkles}
          title={t.productCreative.styleAutoTitle}
          subtitle={t.productCreative.styleAutoSubtitle}
          isActive={mode === "auto"}
          onClick={() => onModeChange("auto")}
        />
        <ModeCard
          icon={Palette}
          title={t.productCreative.styleManualTitle}
          subtitle={t.productCreative.styleManualSubtitle}
          isActive={mode === "manual"}
          onClick={() => onModeChange("manual")}
        />
      </div>

      {mode === "manual" && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
          <label className="block mb-2 text-sm font-medium text-foreground">
            {t.productCreative.customDirectionLabel}
          </label>
          <textarea
            value={customDirection}
            onChange={(e) => onCustomDirectionChange(e.target.value)}
            placeholder={t.productCreative.customDirectionPlaceholder}
            rows={4}
            maxLength={2000}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none"
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground/80 text-end">
            {customDirection.length} / 2000
          </p>
        </div>
      )}
    </div>
  );
}

function ModeCard({
  icon: Icon,
  title,
  subtitle,
  isActive,
  onClick,
}: {
  icon: typeof Sparkles;
  title: string;
  subtitle: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "group flex flex-col items-start gap-2 p-4 md:p-5 rounded-2xl border text-start transition-all duration-200",
        isActive
          ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary shadow-md ring-2 ring-primary/30"
          : "bg-card border-border hover:border-primary/40 hover:shadow-sm md:hover:-translate-y-0.5",
      )}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
          isActive ? "bg-primary text-white" : "bg-primary/10 text-primary",
        )}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="space-y-0.5">
        <p className="text-sm md:text-base font-semibold text-foreground">{title}</p>
        <p className="text-xs md:text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </button>
  );
}
